import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Form, Card } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { FaFilePdf, FaFileWord, FaFileImage, FaFile, FaExternalLinkAlt, FaArrowLeft } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { supportApi, Ticket } from './SupportApi';
import ConversationAvatar from './ConversationAvatar';
import DragDropFileUpload from './DragDropFileUpload';
import { useApiLoader } from "../../../hooks/useApiLoader";
import "./SupportTicketDetails.scss";

const SupportTicketDetails = () => {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const { withLoader } = useApiLoader();

  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [replyFiles, setReplyFiles] = useState<FileList | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (ticketId) {
      fetchTicketDetails();
    }
  }, [ticketId]);

  const fetchTicketDetails = async () => {
    try {
      setLoading(true);
      const response = await withLoader(
        () => supportApi.getTicketDetails(ticketId!),
        'Loading ticket details...'
      );

      if (response.status === 'Success' && response.data) {
        setTicket(response.data);
      } else {
        toast.error(response.message || 'Failed to load ticket details');
      }
    } catch (error: any) {
      toast.error('Failed to load ticket details');
    } finally {
      setLoading(false);
    }
  };

  const handleReplySubmit = async () => {
    if (!replyText.trim()) {
      toast.error('Please enter a reply message');
      return;
    }

    try {
      setSubmitting(true);

      const response = await withLoader(
        () => supportApi.replyToTicket(ticketId!, {
          message: replyText,
          files: replyFiles
        }),
        'Sending reply...'
      );

      if (response.status === 'Success') {
        toast.success('Reply sent successfully');
        setReplyText('');
        setReplyFiles(null);

        // Refresh ticket details to show new reply
        await fetchTicketDetails();
      } else {
        toast.error(response.message || 'Failed to send reply');
      }

    } catch (error: any) {
      toast.error('Failed to send reply');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    try {
      const response = await withLoader(
        () => supportApi.updateTicketStatus(ticketId!, { status: newStatus }),
        'Updating ticket status...'
      );

      if (response.status === 'Success') {
        toast.success(`Ticket status updated to ${newStatus}`);
        if (ticket) {
          setTicket({ ...ticket, status: newStatus });
        }
      } else {
        toast.error(response.message || 'Failed to update ticket status');
      }
    } catch (error: any) {
      toast.error('Failed to update ticket status');
    }
  };

  const getFileInfo = (url: string, attachIndex: number) => {
    const fileName = url.split('/').pop() || `attachment-${attachIndex + 1}`;
    const extension = fileName.split('.').pop()?.toLowerCase() || '';
    const fileType = extension === 'pdf' ? 'PDF' :
                  extension === 'doc' || extension === 'docx' ? 'DOC' :
                  ['jpg', 'jpeg', 'png', 'gif'].includes(extension) ? 'IMG' : 'FILE';
    return { fileName, extension, fileType };
  };

  const renderFileIcon = (fileType: string) => {
    switch (fileType) {
      case 'PDF': return <FaFilePdf style={{ color: '#dc3545' }} />;
      case 'DOC': return <FaFileWord style={{ color: '#0d6efd' }} />;
      case 'IMG': return <FaFileImage style={{ color: '#198754' }} />;
      default: return <FaFile style={{ color: '#6c757d' }} />;
    }
  };

  const getStatusBadge = (status: string) => {
    let badgeClass = '';
    let displayText = status;

    switch (status?.toLowerCase()) {
      case 'open':
        badgeClass = 'badge bg-warning text-dark';
        displayText = 'Open';
        break;
      case 'in-progress':
        badgeClass = 'badge bg-primary';
        displayText = 'In Progress';
        break;
      case 'completed':
        badgeClass = 'badge bg-success';
        displayText = 'Completed';
        break;
      default:
        badgeClass = 'badge bg-secondary';
        displayText = status || 'Unknown';
    }
    return <span className={badgeClass}>{displayText}</span>;
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <section className='main-body'>
        <Container fluid className="p-0">
          <Card.Body>
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Loading ticket details...</p>
            </div>
          </Card.Body>
        </Container>
      </section>
    );
  }

  if (!ticket) {
    return (
      <section className='main-body'>
        <Container fluid className="p-0">
          <Card.Body>
            <div className="text-center py-5">
              <h4>Ticket not found</h4>
              <Button variant="primary" onClick={() => navigate('/support')}>
                Back to Support
              </Button>
            </div>
          </Card.Body>
        </Container>
      </section>
    );
  }

  return (
    <section className='main-body'>
      <Container fluid className="p-0">
        <Card.Body>
          <div className="support-ticket-details">
            {/* Header Section with Admin Pattern */}
            <div className="ticket-header-section">
              <div className="container-fluid">
                <div className="row align-items-center mb-4">
                  <div className="col-md-8">
                    <nav className="breadcrumb-nav">
                      <div className="d-flex align-items-center gap-3">
                        <Button
                          variant="link"
                          className="text-decoration-none p-0 fw-bold d-flex align-items-center gap-2"
                          style={{ color: '#3957A0' }}
                          onClick={() => navigate('/support')}
                        >
                          <FaArrowLeft /> Back to Support
                        </Button>
                        <nav aria-label="breadcrumb">
                          <ol className="breadcrumb mb-0">
                            <li className="breadcrumb-item">
                              <span className="text-muted">Support</span>
                            </li>
                            <li className="breadcrumb-item active">
                              #{ticket.ticketId}
                            </li>
                          </ol>
                        </nav>
                      </div>
                    </nav>
                  </div>
                  <div className="col-md-4 text-end">
                    <span className="text-muted me-3">Posted: {formatDate(ticket.supportTicketRaisedDate || ticket.createdAt || '')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Title Section */}
            <div className="ticket-title-section" style={{ backgroundColor: '#EFF4FF', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
              <div className="container-fluid">
                <div className="row align-items-center">
                  <div className="col-md-8">
                    <h2 className="mb-2 fw-bold" style={{ color: '#3957A0', fontSize: '24px' }}>
                      #{ticket.ticketId} - {ticket.subject || ticket.title || 'No Subject'}
                    </h2>
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                      <div className="d-flex align-items-center gap-2">
                        <span className="text-muted">Customer:</span>
                        <strong style={{ color: '#2c3e50' }}>{ticket.userName || ticket.user?.name || 'Unknown User'}</strong>
                      </div>
                      <span className="text-muted">•</span>
                      <span className="text-muted">{ticket.userEmail || ticket.user?.email || 'No email'}</span>
                      <span className="text-muted">•</span>
                      <span className="text-muted">ID: {ticket.user?.userId || ticket.id}</span>
                    </div>
                  </div>
                  <div className="col-md-4 text-end">
                    <div className="d-flex align-items-center justify-content-end gap-3">
                      {getStatusBadge(ticket.status)}
                      <Form.Select
                        size="sm"
                        value={ticket.status}
                        onChange={(e) => handleStatusChange(e.target.value)}
                        style={{ width: 'auto', minWidth: '150px' }}
                        className="border-0"
                      >
                        <option value="open">Open</option>
                        <option value="in-progress">In Progress</option>
                        <option value="completed">Completed</option>
                      </Form.Select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Ticket Details Card - Matching User UI */}
            <div className="issue-detail-card">
              <div className="issue-header">
                <div className="issue-description mb-4">
                  <h5 className="mb-3" style={{ color: '#3957A0', fontSize: '16px', fontWeight: '600' }}>Description:</h5>
                  <p style={{ color: '#677289', fontSize: '15px', lineHeight: '1.6' }}>{ticket.description || ticket.subject || ticket.title || 'No description available'}</p>
                </div>
              </div>

              {/* Conversation Thread - Same structure as User UI */}
              <div className="conversation-thread">
                {ticket.conversation?.map((message, index) => (
                  <div key={message._id || index} className="message-item">
                    <div className="message-header">
                      <ConversationAvatar
                        author={message.sender === 'user' ? (ticket.userName || ticket.user?.name || 'Unknown User') : 'Support Team'}
                        isAdmin={message.sender !== 'user'}
                        profileImage={message.sender === 'user' ? message.profileUrl : message.adminProfileUrl}
                      />
                      <div className="message-meta">
                        <strong className="author-name">
                          {message.sender === 'user' ? (ticket.userName || ticket.user?.name || 'Unknown User') : 'Support Team'}
                        </strong>
                        <span className="message-time">
                          {formatDate(message.timestamp)}
                        </span>
                      </div>
                    </div>
                    <div className="message-content">{message.message}</div>

                    {message.attachments && message.attachments.length > 0 && (
                      <div className="message-attachments mt-3">
                        <div className="attachments-grid">
                          {message.attachments.map((attachment: string, attachIndex: number) => {
                            const { fileName, extension, fileType } = getFileInfo(attachment, attachIndex);
                            return (
                              <div key={attachIndex} className="attachment-card">
                                <div className="attachment-icon">
                                  {renderFileIcon(fileType)}
                                </div>
                                <div className="attachment-info">
                                  <div className="attachment-name" title={fileName}>
                                    {fileName.length > 25 ? fileName.substring(0, 25) + '...' : fileName}
                                  </div>
                                  <div className="attachment-type">
                                    {fileType} File
                                  </div>
                                </div>
                                <a
                                  href={attachment}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="attachment-download-btn"
                                  title="View Attachment"
                                >
                                  <FaExternalLinkAlt />
                                </a>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Reply Form - Matching User UI Structure */}
              <div className="reply-form">
                <h5>Reply to Ticket</h5>
                <Form.Group className="mb-3">
                  <Form.Label>Reply *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder="Write your response..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Upload File</Form.Label>
                  <DragDropFileUpload
                    onFilesSelected={setReplyFiles}
                    currentFiles={replyFiles}
                  />
                </Form.Group>

                <Button
                  className="main-bttn btn-primary w-auto px-5"
                  onClick={handleReplySubmit}
                  disabled={!replyText.trim() || submitting}
                >
                  {submitting ? 'Sending...' : 'Reply'}
                </Button>
              </div>
            </div>
          </div>
        </Card.Body>
      </Container>
    </section>
  );
};

export default SupportTicketDetails;